import * as React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  LucideIcon,
  ArrowRight,
  Lock,
  Globe,
  User,
  Calendar,
} from "lucide-react";
import { cn } from "@jambushrushti/lib/utils";

export interface ContentCardProps {
  id: string | number;
  href: string;
  image: string;
  title: string;
  subtitle?: string;
  description?: string;
  category?: string;
  categoryIcon?: LucideIcon;
  badges?: Array<{
    label: string;
    variant?: "default" | "secondary" | "outline";
  }>;
  metadata?: Array<{ icon: LucideIcon; label: string }>;
  tags?: string[];
  isRestricted?: boolean;
  isAvailable?: boolean;
  actionLabel?: string;
  aspectRatio?: "square" | "video" | "portrait";
  className?: string;
  index?: number;
}

export const ContentCard = React.forwardRef<
  HTMLAnchorElement,
  ContentCardProps
>(
  (
    {
      id,
      href,
      image,
      title,
      subtitle,
      description,
      category,
      categoryIcon: CategoryIcon,
      badges = [],
      metadata = [],
      tags = [],
      isRestricted = false,
      isAvailable,
      actionLabel = "View Details",
      aspectRatio = "portrait",
      className,
      index = 0,
    },
    ref
  ) => {
    const aspectClasses = {
      square: "aspect-square",
      video: "aspect-video",
      portrait: "aspect-[3/4]",
    };

    return (
      <Link ref={ref} to={href} className={cn("group block h-full", className)}>
        <Card
          variant="interactive"
          className="animate-fade-up h-full overflow-hidden"
          style={{ animationDelay: `${(index % 12) * 50}ms` }}
        >
          {/* Cover Image */}
          <div
            className={cn(
              "relative overflow-hidden bg-secondary/30",
              aspectClasses[aspectRatio]
            )}
          >
            <img
              src={image}
              alt={title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />

            {/* Top Badges */}
            <div className="absolute left-3 right-3 top-3 flex items-start justify-between gap-2">
              {category && (
                <Badge
                  variant="secondary"
                  className="border-0 bg-background/90 text-xs backdrop-blur-sm"
                >
                  {CategoryIcon && <CategoryIcon className="mr-1 h-3 w-3" />}
                  {category}
                </Badge>
              )}

              {isAvailable !== undefined && (
                <Badge
                  variant="outline"
                  className={cn(
                    "bg-background/90 text-xs backdrop-blur-sm",
                    isAvailable
                      ? "text-sage border-sage/50"
                      : "border-border text-muted-foreground"
                  )}
                >
                  {isAvailable ? "Available" : "Reference Only"}
                </Badge>
              )}

              {isRestricted && (
                <Badge
                  variant="outline"
                  className="bg-background/90 text-xs backdrop-blur-sm"
                >
                  <Lock className="mr-1 h-3 w-3" />
                  Restricted
                </Badge>
              )}
            </div>
          </div>

          {/* Content */}
          <CardContent className="flex flex-col p-4">
            {/* Custom Badges */}
            {badges.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {badges.map((badge, i) => (
                  <Badge
                    key={i}
                    variant={badge.variant || "secondary"}
                    className="text-xs"
                  >
                    {badge.label}
                  </Badge>
                ))}
              </div>
            )}

            {/* Title */}
            <h3 className="font-display mb-1 line-clamp-2 text-base font-semibold leading-tight text-foreground transition-colors group-hover:text-primary">
              {title}
            </h3>

            {/* Subtitle */}
            {subtitle && (
              <p className="mb-2 line-clamp-1 text-xs font-medium text-primary">
                {subtitle}
              </p>
            )}

            {/* Description */}
            {description && (
              <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                {description}
              </p>
            )}

            {/* Metadata */}
            {metadata.length > 0 && (
              <div className="mb-3 space-y-1">
                {metadata.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="line-clamp-1">{item.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-1">
                {tags.slice(0, 3).map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Action */}
            <div className="mt-auto border-t border-border pt-3">
              {isRestricted ? (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" />
                  <span>Sign In to Access</span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-xs text-primary group-hover:underline">
                    {actionLabel}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }
);

ContentCard.displayName = "ContentCard";

// Compact variant for smaller grids
export interface CompactCardProps {
  href: string;
  image: string;
  title: string;
  subtitle?: string;
  badge?: string;
  index?: number;
  className?: string;
}

export const CompactCard = React.forwardRef<
  HTMLAnchorElement,
  CompactCardProps
>(({ href, image, title, subtitle, badge, index = 0, className }, ref) => {
  return (
    <Link ref={ref} to={href} className={cn("group block", className)}>
      <Card
        variant="interactive"
        className="animate-fade-up overflow-hidden"
        style={{ animationDelay: `${(index % 12) * 50}ms` }}
      >
        <div className="relative aspect-square overflow-hidden bg-secondary/30">
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
          {badge && (
            <Badge
              variant="secondary"
              className="absolute left-2 top-2 border-0 bg-background/90 text-[10px] backdrop-blur-sm"
            >
              {badge}
            </Badge>
          )}
        </div>
        <CardContent className="p-3">
          <h3 className="font-display line-clamp-2 text-sm font-medium text-foreground transition-colors group-hover:text-primary">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
              {subtitle}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
});

CompactCard.displayName = "CompactCard";
